/** @jsxImportSource @emotion/react */
import 'react-app-polyfill/ie11'
import 'react-app-polyfill/stable'
import './environment.ts'
import React from 'react'
import ReactDOM from 'react-dom'
import { BrowserRouter } from 'react-router-dom'
import AppToastr from '@signal/components/baseline/AppToastr'
import ScrollToTop from '@signal/components/baseline/ScrollToTop'
import { AppModalPromptGenerator } from '@signal/components/ModalPrompt'
import applyMiddleware from '@signal/core/baseline/data/middleware/apply-middleware'
import applyPublicMiddleware from '@signal/core/baseline/data/middleware/apply-public-middleware'
import { setNeedLegacyAuth } from '@signal/core/baseline/data/middleware/auth-redirect'
import fetchWithCredentials from '@signal/core/baseline/data/utils/fetch-with-credentials'
import InstrumentationRoot from '@signal/core/instrumentation/Root'
import { ACCOUNT_ID_REDIR_SUBST } from '@signal/core/router/redirect'
import SWRRoot from '@signal/core/swr/Root'
import TenantThemeContext from '@signal/core/tenantTheme/Context'
import TenantThemeLoader from '@signal/core/tenantTheme/Loader'
import GlobalStyle from '@signal/theme/baseline/globalStyle'
import ThemeProvider from '@signal/theme/Provider'
import isDevelopment from '@signal/utils/env/isDevelopment'
import getLogger, { initLogger } from '@signal/core/fe-logging'
import renderIf from '@signal/utils/renderIf'
import App from './App'
import UI_BASE_PATH from './base-path'
import { StartAppPropsType } from './types'
import { hookGlobalExceptionLogging } from './utils'
import { ThemeSafeErrorBoundary } from '@signal/components/ThemeSafeErrorBoundary'
import PublicErrorBoundary from './PublicErrorBoundary'

const middlewareFetcher = applyMiddleware()(fetchWithCredentials)

/**
 * Baseline-derived apps should call this to render their application
 * @param {Component} ErrorPage - an optional catch-all error page to use
 * @param {Array} appRoutes - the APP_ROUTES from your baseline app
 * @param {String} menuBasePath - the base path for this application when in the menu
 * @param {Boolean} noTransitions - if true, don't use the transitions from react-transition-group on route change
 * ... also need to provide a number of react-router related components and hooks
 */
export const startApp = (props: StartAppPropsType): void => {
  const { standardRedirect = `/${ACCOUNT_ID_REDIR_SUBST}` } = props
  const { appSupportedQP = [] } = props
  const isPublic = !!props.public

  if (isDevelopment()) {
    // @ts-ignore
    window.EMOTION_RUNTIME_AUTO_LABEL = true
  }

  if (!isPublic) {
    setNeedLegacyAuth(props.needLegacyAuth)
  }

  // Not at module scope: only a public app needs the credential-less fetch and
  // the stack without auth-redirect, so 401/403 stays out of the Sheriff login.
  const fetcher = isPublic
    ? applyPublicMiddleware(fetch)()
    : middlewareFetcher()

  initLogger(props.projectName)

  hookGlobalExceptionLogging()

  const ExtraAppStack = props.ExtraAppStack || React.Fragment

  const appTree = (
    <AppToastr>
      <AppModalPromptGenerator>
        <ExtraAppStack>
          <App
            standardRedirect={standardRedirect}
            {...props}
            appSupportedQP={appSupportedQP}
          />
        </ExtraAppStack>
      </AppModalPromptGenerator>
    </AppToastr>
  )

  const ConnectedApp = () => (
    // TODO - Figure out why BrowserRouter types does not include children within props
    // @ts-ignore
    <BrowserRouter basename={UI_BASE_PATH || '/'}>
      <ScrollToTop />
      <SWRRoot useCredentials={!isPublic} fetcher={fetcher}>
        <InstrumentationRoot
          appName={props.appName}
          instrumentationId={props.instrumentationId}
          waitForPageInstrumentationFromApp={
            props.waitForPageInstrumentationFromApp
          }
        >
          {isPublic ? (
            <PublicErrorBoundary>
              {/* No TenantThemeLoader: a public app resolves its own branding */}
              <ThemeProvider>
                <GlobalStyle />
                {appTree}
              </ThemeProvider>
            </PublicErrorBoundary>
          ) : (
            <ThemeSafeErrorBoundary redirectPath={standardRedirect}>
              {/* TODO - Figure out how to handle types coming from our contexts */}
              <TenantThemeLoader>
                <TenantThemeContext.Consumer>
                  {({ response: tenantTheme }) =>
                    renderIf(!!tenantTheme, () => (
                      <ThemeProvider tenantTheme={tenantTheme}>
                        <GlobalStyle />
                        {appTree}
                      </ThemeProvider>
                    ))
                  }
                </TenantThemeContext.Consumer>
              </TenantThemeLoader>
            </ThemeSafeErrorBoundary>
          )}
        </InstrumentationRoot>
      </SWRRoot>
    </BrowserRouter>
  )

  // @ts-ignore
  if (ReactDOM.createRoot) {
    // the React 18 way to kickoff the app render
    // @ts-ignore
    ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
      <ConnectedApp />
    )
  } else {
    // the pre-React 18 way to kickoff the app render
    // TODO: once all apps are on React 18, we can remove this and import createRoot from react-dom/client.
    ReactDOM.render(<ConnectedApp />, document.getElementById('root'))
  }

  const preload = document.getElementById('preload')
  if (preload && preload.remove) {
    preload.remove()
  } else if (preload) {
    document.body.removeChild(preload)
  }

  getLogger().info(
    'FE app started after {loadTime}ms',
    Date.now() - performance.timeOrigin
  )
}

export default startApp
